import { useContext, createContext, useState, useEffect, useRef } from "react";

export const MeetingAppContext = createContext();

export const useMeetingAppContext = () => useContext(MeetingAppContext);

export const MeetingAppProvider = ({ children }) => {
  const [selectedMic, setSelectedMic] = useState({ id: null, label: null });
  const [selectedWebcam, setSelectedWebcam] = useState({ id: null, label: null });
  const [selectedSpeaker, setSelectedSpeaker] = useState({ id: null, label: null });
  const [isCameraPermissionAllowed, setIsCameraPermissionAllowed] = useState(null);
  const [isMicrophonePermissionAllowed, setIsMicrophonePermissionAllowed] = useState(null);
  const [raisedHandsParticipants, setRaisedHandsParticipants] = useState([]);
  const [sideBarMode, setSideBarMode] = useState(null);
  const [leftSideBarMode, setLeftSideBarMode] = useState(null);
  const [pipMode, setPipMode] = useState(false);
  const [pipClosedByUser, setPipClosedByUser] = useState(false);
  const restoreScreenSharePipRef = useRef(null);
  const [unreadChat, setUnreadChat] = useState(false);
  const [recentReactions, setRecentReactions] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState("en");
  const [translationEnabled, setTranslationEnabled] = useState(false);
  const [transcriptionEnabled, setTranscriptionEnabled] = useState(false);
  const [virtualBackgroundOn, setVirtualBackgroundOn] = useState(false);
  const [selectedBackground, setSelectedBackground] = useState(null);
  const [blurEnabled, setBlurEnabled] = useState(false);
  const [participantApproveFunc, setParticipantApproveFunc] = useState([]);
  const [approvalSoundEnabled, setApprovalSoundEnabled] = useState(true);
  const [afterMeetingJoinedHLSState, setAfterMeetingJoinedHLSState] =
    useState(null);
  const [participantWebinarFunc, setParticipantWebinarFunc] = useState([]);
  const [activeSpeakerId, setActiveSpeakerId] = useState(null);
  const [currentFocusParticipantId, setCurrentFocusParticipantId] =
    useState(null);
  const [showRecordingConsentModal, setShowRecordingConsentModal] =
    useState(false);
  const [isParticipantRowMinimized, setIsParticipantRowMinimized] =
    useState(false);

  const useRaisedHandParticipants = () => {
    const raisedHandsParticipantsRef = useRef();

    const participantRaisedHand = (participantId) => {
      const raisedHandsParticipants = [...raisedHandsParticipantsRef.current];

      const participantFound = raisedHandsParticipants.findIndex(
        ({ participantId: pID }) => pID === participantId
      );

      let action;
      if (participantFound === -1) {
        // Participant is raising their hand
        const newItem = { participantId, raisedHandOn: new Date().getTime() };
        raisedHandsParticipants.push(newItem);
        action = "raised";
      } else {
        // Participant is lowering their hand (toggle off)
        raisedHandsParticipants.splice(participantFound, 1);
        action = "lowered";
      }

      setRaisedHandsParticipants(raisedHandsParticipants);
      return action;
    };

    useEffect(() => {
      raisedHandsParticipantsRef.current = raisedHandsParticipants;
    }, [raisedHandsParticipants]);

    const _handleRemoveOld = () => {
      const raisedHandsParticipants = [...raisedHandsParticipantsRef.current];

      const now = new Date().getTime();

      const persisted = raisedHandsParticipants.filter(({ raisedHandOn }) => {
        return parseInt(raisedHandOn) + 15000 > parseInt(now);
      });

      if (raisedHandsParticipants.length !== persisted.length) {
        setRaisedHandsParticipants(persisted);
      }
    };

    useEffect(() => {
      const interval = setInterval(_handleRemoveOld, 1000);

      return () => {
        clearInterval(interval);
      };
    }, []);

    return { participantRaisedHand };
  };

  return (
    <MeetingAppContext.Provider
      value={{
        // states

        raisedHandsParticipants,
        selectedMic,
        selectedWebcam,
        selectedSpeaker,
        sideBarMode,
        leftSideBarMode,
        pipMode,
        pipClosedByUser,
        restoreScreenSharePipRef,
        isCameraPermissionAllowed,
        isMicrophonePermissionAllowed,

        // setters

        setRaisedHandsParticipants,
        setSelectedMic,
        setSelectedWebcam,
        setSelectedSpeaker,
        setSideBarMode,
        setLeftSideBarMode,
        setPipMode,
        setPipClosedByUser,
        useRaisedHandParticipants,
        setIsCameraPermissionAllowed,
        setIsMicrophonePermissionAllowed,
        setUnreadChat,
        unreadChat,
        setRecentReactions,
        recentReactions,
        selectedLanguage,
        setSelectedLanguage,
        setTranslationEnabled,
        translationEnabled,
        setTranscriptionEnabled,
        transcriptionEnabled,
        virtualBackgroundOn,
        setVirtualBackgroundOn,
        selectedBackground,
        setSelectedBackground,
        blurEnabled,
        setBlurEnabled,
        participantApproveFunc,
        setParticipantApproveFunc,
        approvalSoundEnabled,
        setApprovalSoundEnabled,
        afterMeetingJoinedHLSState,
        setAfterMeetingJoinedHLSState,
        participantWebinarFunc,
        setParticipantWebinarFunc,
        activeSpeakerId,
        setActiveSpeakerId,
        currentFocusParticipantId,
        setCurrentFocusParticipantId,
        showRecordingConsentModal,
        setShowRecordingConsentModal,
        isParticipantRowMinimized,
        setIsParticipantRowMinimized,
      }}
    >
      {children}
    </MeetingAppContext.Provider>
  );
};
